import { createMemo, createSignal, Show } from "solid-js";
import { useNavigate } from "@solidjs/router";
import { padId } from "~/lib";
import { runDeleteCommand } from "~/_server";
import { useToast } from "~/_context/ToastProvider";

import { SquarePen, Printer, Trash2 as Delete, Bar } from "lucide-solid";

export const Certificate = ( props: any ) => {
	const navigate = useNavigate();
	const { addToast, toastTypes } = useToast();
	const [deleteable, setDeleteable] = createSignal<boolean>( false );
	const [deleted, setDeleted] = createSignal<boolean>( false );

	if ( typeof props.certificate == "undefined" )
		return;

	const certificate = createMemo( () =>
		typeof props.certificate === "function"
			? props.certificate()
			: props.certificate
	);

	const memo = createMemo( () => {
		const isDraft = certificate().status === "draft";
		const isPaid = certificate().status === "paid";
		const isPublished = certificate().status === "published" || isPaid;
		const isStaged = !!certificate().meta.isStagedCertificate;

		const stage = props.full ? "Stage " : "S";
		const amendment = props.full ? "Amendment " : "A";

		const stageCount = certificate().meta?.stages?.length ?? 0;
		const amendCount = certificate().meta?.amendments?.length ?? 0;

		let link = certificate().riasId ? padId( certificate().riasId ) : "";

		let state = {
			projectId: certificate().parent.replace( 'PROJECT#', '' ),
			certificateId: null,
			amendment: 0,
			stage: 0,
		};

		if ( isDraft ) {
			state.amendment = ( certificate().meta?.amendments?.length ?? 0 );
			state.stage = ( certificate().meta?.stages?.length ?? 0 );

		} else {
			state.certificateId = certificate().sk;

			if ( isStaged ) {
				state.stage = ( certificate().meta?.stages?.length ?? 0 ) + 1;
			} else {
				state.amendment = ( certificate().meta?.amendments?.length ?? 0 ) + 1;
			}
		}

		if ( stageCount ) link += `-Stage${stageCount}`;

		if ( amendCount ) link += `-Amendment${amendCount}`;

		return { state, stage, amendment, stageCount, amendCount, link, isPaid, isPublished, isStaged, isDraft };
	} )

	/*
	href={`/certificate/C${memo().link}`}*/

	return (
		<div class="cert-options group inline-flex flex-nowrap items-center justify-between gap-x-1.5 mr-2 my-1 relative rounded-md bg-gray-100 px-2 py-1 font-medium text-gray-600 hover:bg-primary hover:text-white transition-colors duration-300"
			classList={{ "text-3xl": !props.slimView, "text-lg": props.slimView }}
		>
			<svg viewBox="0 0 12 12" aria-hidden="true" class="size-3.5 mx-1 mt-1.5"
				classList={{
					"fill-red-400": !memo().isPublished,
					"fill-yellow-400": memo().isPublished && !memo().isPaid,
					"fill-green-400": memo().isPaid,
					"opacity-20": deleted(),
				}}>
				<circle r="5" cx="5" cy="5" />
			</svg>

			<span class="mr-8 text-nowrap">
				C{padId( props.certificate?.riasId )}
				{memo().stageCount ? ` - ${memo().stage}${memo().stageCount}` : ""}
				{memo().amendCount ? ` - ${memo().amendment}${memo().amendCount}` : ""}
			</span>

			{/*}
			<Show when={props.certificate?.status == "draft" && props.full === true}>
				<div
					onClick={async () => {
						if ( !deleteable() ) {
							setDeleteable( true );
						} else {
							const r = await runDeleteCommand(
								props.certificate.pk,
								props.certificate.sk,
							);

							if ( !r.statusCode || r.statusCode == -1 ) {
								addToast( r.error, toastTypes.error );
							} else {
								setDeleted( true );
								addToast( "Certificate Deleted", toastTypes.success );
								props.revalidate();
							}
						}
					}}
					class="transition-all left-1 group-hover:left-4/5 group-hover:z-15 absolute cursor-pointer border top-0 h-full rounded-md group-hover:rounded-l-none  pl-8 pr-2 py-1 text-lg font-medium "
					classList={{
						"bg-gray-500": !deleteable(),
						"bg-white": deleteable(),
						"border-gray-500": !deleteable(),
						"border-red-700": deleteable(),
						"text-white": !deleteable(),
						"text-red-700": deleteable(),
					}}>
					<Trash height="auto" width="20px" />
				</div>
			</Show>
			*/}

			<Show when={memo().isDraft}>
				<span class="icon" title="Edit Certificate" onClick={() => navigate( `/dashboard/certificate/${certificate().sk.replace( 'CERTIFICATE#', '' )}`, { state: memo().state } )}>
					<SquarePen height="auto" width="30px" />
				</span>
			</Show>

			<span class="icon">
				<a href={`/certificate/C${memo().link}?print=true`} target="_blank" rel="noopener noreferrer" title="Print Certificate">
					<Printer height="auto" width="30px" />
				</a>
			</span>

			<Show when={memo().isDraft && props.full === true}>
				<span class="icon" title="Delete Certificate"
					onClick={async () => {
						if ( !deleteable() ) {
							setDeleteable( true );

							setTimeout( () => {
								setDeleteable( false );
							}, 3000 );
						} else {
							const r = await runDeleteCommand(
								certificate().pk,
								certificate().sk,
							);

							if ( !r.statusCode || r.statusCode == -1 ) {
								addToast( r.error, toastTypes.error );
							} else {
								setDeleted( true );
								addToast( "Certificate Deleted", toastTypes.success );
								props.revalidate();
							}
						}
					}}>
					<Delete height="auto" width="30px" classList={{"hover:stroke-red-300":deleteable()}} />
				</span>
			</Show>

			<Show when={memo().isPaid && props.isLatest}>
				<Show when={memo().isStaged}>
					<span class="icon text-sm" title="Add Stage" onClick={() => navigate( `/dashboard/certificate/new`, { state: memo().state } )}>+Stage</span>
				</Show>

				<span class="icon text-sm" title="Add Amendment" onClick={() => navigate( `/dashboard/certificate/new`, { state: memo().state } )}>+Amendment</span>
			</Show>
		</div>
	);
};
